/**
 * 計助: 96穴プレート（CPE +/−）一括解析パネル。
 *
 * Excel/ods の該当ブロック（sample名 行 + 希釈段 + +/− 本体）を貼り付けると、
 * `keisukePlateBatch` エンジンが「sample名」ヘッダ行でサンプルを分割・集計し、
 * サンプルごとに TCID50 / LD50 / EC50 / EC90 を一括算出する。
 * 結果行をクリックするとワークスペース表・グラフ・論文タブへ反映できる。
 */

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button } from 'react-bootstrap';
import {
  buildPlateWorkspacePayload,
  parsePlate,
  runPlateBatch,
  plateBatchToCsv,
  type PlateAnalysisType,
  type PlateWarning,
  type PlateBatchRow,
  type PlateWorkspacePayload,
} from '../../../../utils/keisukePlateBatch';
import type { Method } from '../../../../utils/statistics/doseResponse';
import { KEISUKE_DASHBOARD_NEW_EVENT } from './KeisukeDashboardBarChartSettingsContext';
import styles from './KeisukeDashboardCenterPane.module.scss';

interface Props {
  /** 解析タイプの初期値（用量反応の drType から渡す） */
  initialAnalysis?: PlateAnalysisType;
  /** 結果表の行クリックでワークスペースへ1サンプルを載せる */
  onLoadSample?: (payload: PlateWorkspacePayload) => void;
}

const METHODS_BY_ANALYSIS: Record<PlateAnalysisType, Method[]> = {
  tcid50: ['spearman-karber', 'reed-muench'],
  ld50: ['probit', 'logistic', 'reed-muench', 'spearman-karber'],
  ec50: ['probit', 'logistic', 'reed-muench', 'spearman-karber'],
  ec90: ['probit', 'logistic', 'reed-muench', 'spearman-karber'],
};

const METHOD_LABEL_KEY: Record<Method, string> = {
  'spearman-karber': 'keisuke.plateBatch.method.spearmanKarber',
  'reed-muench': 'keisuke.plateBatch.method.reedMuench',
  probit: 'keisuke.plateBatch.method.probit',
  logistic: 'keisuke.plateBatch.method.logistic',
};

const ANALYSIS_OPTIONS: PlateAnalysisType[] = ['tcid50', 'ld50', 'ec50', 'ec90'];

export default function KeisukePlateBatchPanel({
  initialAnalysis = 'tcid50',
  onLoadSample,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(initialAnalysis === 'tcid50');
  const [text, setText] = useState('');
  const [analysis, setAnalysis] = useState<PlateAnalysisType>(initialAnalysis);
  const [method, setMethod] = useState<Method>(METHODS_BY_ANALYSIS[initialAnalysis][0]!);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setAnalysis(initialAnalysis);
    const allowed = METHODS_BY_ANALYSIS[initialAnalysis];
    setMethod((m) => (allowed.includes(m) ? m : allowed[0]!));
    if (initialAnalysis === 'tcid50') setOpen(true);
  }, [initialAnalysis]);

  useEffect(() => {
    const onNew = () => {
      setText('');
      setOpen(false);
      setCopied(false);
    };
    window.addEventListener(KEISUKE_DASHBOARD_NEW_EVENT, onNew);
    return () => window.removeEventListener(KEISUKE_DASHBOARD_NEW_EVENT, onNew);
  }, []);

  /** 警告コード → 表示文言 */
  const warnLabel = useCallback(
    (w: PlateWarning): string => {
      const map: Record<string, string> = {
        'plate.noDataRows': 'keisuke.plateBatch.warn.noDataRows',
        'plate.noSampleRow': 'keisuke.plateBatch.warn.noSampleRow',
        'plate.noSamples': 'keisuke.plateBatch.warn.noSamples',
        'plate.nonMonotonic': 'keisuke.plateBatch.warn.nonMonotonic',
        'plate.calc.minTwoPoints': 'keisuke.plateBatch.warn.minTwoPoints',
      };
      const key = map[w.code];
      if (key) return t(key);
      return t('keisuke.plateBatch.warn.calcFailed', { code: w.code.replace(/^plate\.calc\./, '') });
    },
    [t]
  );

  const parsed = useMemo(() => (text.trim() ? parsePlate(text) : null), [text]);

  const rows: PlateBatchRow[] = useMemo(() => {
    if (!parsed || parsed.samples.length === 0) return [];
    return runPlateBatch(parsed.samples, { analysis, method });
  }, [parsed, analysis, method]);

  const onAnalysisChange = useCallback((next: PlateAnalysisType) => {
    setAnalysis(next);
    const allowed = METHODS_BY_ANALYSIS[next];
    setMethod((m) => (allowed.includes(m) ? m : allowed[0]!));
  }, []);

  const fmt = (n: number | null, digits = 4): string =>
    n === null || !Number.isFinite(n) ? '—' : Number(n.toPrecision(digits)).toString();

  const handleCopyCsv = useCallback(async () => {
    if (rows.length === 0) return;
    const csv = plateBatchToCsv(rows, analysis);
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* クリップボード不可環境は黙って無視（ダウンロードで代替可能） */
    }
  }, [rows, analysis]);

  const handleDownloadCsv = useCallback(() => {
    if (rows.length === 0) return;
    const csv = plateBatchToCsv(rows, analysis);
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keisuke_${analysis}_batch_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, analysis]);

  const handleLoadRow = useCallback(
    (row: PlateBatchRow) => {
      if (!onLoadSample || !parsed || row.value === null) return;
      const sample = parsed.samples.find((s) => s.name === row.sample);
      if (!sample) return;
      onLoadSample(buildPlateWorkspacePayload(sample, analysis, method));
    },
    [onLoadSample, parsed, analysis, method]
  );

  const valueHeader = analysis === 'tcid50' ? 'TCID50' : analysis.toUpperCase();

  return (
    <div className={styles.plateBatch}>
      <button
        type="button"
        className={styles.plateBatchToggle}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <i className={`fas fa-${open ? 'chevron-down' : 'chevron-right'}`} aria-hidden />
        <span className="ms-2 fw-medium">{t('keisuke.plateBatch.title')}</span>
      </button>

      {open && (
        <div className={styles.plateBatchBody}>
          <p className="small text-muted mb-2">{t('keisuke.plateBatch.intro')}</p>
          {onLoadSample && (
            <p className="small text-muted mb-2">{t('keisuke.plateBatch.loadToWorkspaceHint')}</p>
          )}

          <div className="d-flex flex-wrap gap-3 align-items-end mb-2">
            <Form.Group controlId="keisuke-plate-analysis">
              <Form.Label className="small mb-1">{t('keisuke.plateBatch.analysisLabel')}</Form.Label>
              <Form.Select
                size="sm"
                value={analysis}
                onChange={(e) => onAnalysisChange(e.target.value as PlateAnalysisType)}
              >
                {ANALYSIS_OPTIONS.map((a) => (
                  <option key={a} value={a}>
                    {t(`keisuke.plateBatch.analysisOpt.${a}`)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="keisuke-plate-method">
              <Form.Label className="small mb-1">{t('keisuke.plateBatch.methodLabel')}</Form.Label>
              <Form.Select
                size="sm"
                value={method}
                onChange={(e) => setMethod(e.target.value as Method)}
              >
                {METHODS_BY_ANALYSIS[analysis].map((m) => (
                  <option key={m} value={m}>
                    {t(METHOD_LABEL_KEY[m])}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <Form.Group controlId="keisuke-plate-paste" className="mb-2">
            <Form.Label className="small mb-1">{t('keisuke.plateBatch.pasteLabel')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={text}
              spellCheck={false}
              placeholder={t('keisuke.plateBatch.pastePlaceholder')}
              onChange={(e) => setText(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre' }}
            />
          </Form.Group>

          <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
            {parsed && (
              <span className="small text-muted">
                {t('keisuke.plateBatch.detected', { count: parsed.samples.length })}
              </span>
            )}
            <div className="ms-auto d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={() => setText('')} disabled={!text}>
                {t('keisuke.plateBatch.clear')}
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleCopyCsv} disabled={rows.length === 0}>
                {copied ? t('keisuke.plateBatch.copied') : t('keisuke.plateBatch.copyCsv')}
              </Button>
              <Button variant="outline-primary" size="sm" onClick={handleDownloadCsv} disabled={rows.length === 0}>
                {t('keisuke.plateBatch.downloadCsv')}
              </Button>
            </div>
          </div>

          {parsed && parsed.warnings.length > 0 && (
            <ul className={`small mb-2 ${styles.plateBatchWarnList}`}>
              {parsed.warnings.map((w, i) => (
                <li key={i} className="text-warning-emphasis">
                  {warnLabel(w)}
                </li>
              ))}
            </ul>
          )}

          {rows.length > 0 ? (
            <div className="table-responsive">
              <table className={`table table-sm table-bordered small mb-0 ${styles.plateBatchTable}`}>
                <thead>
                  <tr>
                    <th>{t('keisuke.plateBatch.col.sample')}</th>
                    <th className="text-end">{valueHeader}</th>
                    {analysis === 'tcid50' && <th className="text-end">{t('keisuke.plateBatch.col.log10')}</th>}
                    <th className="text-end">{t('keisuke.plateBatch.col.ci')}</th>
                    <th className="text-end">{t('keisuke.plateBatch.col.levels')}</th>
                    <th className="text-end">{t('keisuke.plateBatch.col.replicates')}</th>
                    <th>{t('keisuke.plateBatch.col.warnings')}</th>
                    {onLoadSample && <th>{t('keisuke.plateBatch.col.load')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.sample} className={r.value === null ? 'table-warning' : undefined}>
                      <td>{r.sample}</td>
                      <td className="text-end">{fmt(r.value)}</td>
                      {analysis === 'tcid50' && <td className="text-end">{fmt(r.valueLog10, 4)}</td>}
                      <td className="text-end">
                        {r.ciLower === null || r.ciUpper === null
                          ? '—'
                          : `${fmt(r.ciLower)} – ${fmt(r.ciUpper)}`}
                      </td>
                      <td className="text-end">{r.levels}</td>
                      <td className="text-end">{r.replicates}</td>
                      <td>{r.warnings.map((w) => warnLabel(w)).join('; ')}</td>
                      {onLoadSample && (
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            disabled={r.value === null}
                            onClick={() => handleLoadRow(r)}
                          >
                            {t('keisuke.plateBatch.loadToWorkspace')}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="small text-muted mb-0">
              {text.trim() ? t('keisuke.plateBatch.noResults') : t('keisuke.plateBatch.empty')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
